import React, { FC, useMemo, useRef, useState, useEffect } from 'react';
import { RegistrationContext, RegistrationContextProps } from './Registration.context';
import { defaultRegistrationFormState } from './config';
import { useDeviceInfo } from '../../hooks/useDeviceInfo';
import { APP_URLS } from '../../consts';
import { useNavigate } from 'react-router-dom';
import { joinStrings } from '../../utils/string';
import { Card, CardContent } from '../shared/Card';
import { RegistrationContainer } from './RegistrationContainer';
import { Footer } from '../Footer';
import { BaseRegistrationStepConfig } from '../../hooks/registration';
import { useDispatch } from 'react-redux';
import { setIsGeoLocationAllowed, setIsErrorDuringGeoCheck } from '../../state/app/reducers';
import BingoLogo from '../../assets/Registration/Bingo_Itv_Logo.svg';
import BingoStar from '../../assets/Registration/Bingo_Star.svg';
import SpinsLogo from '../../assets/Registration/Spins_Itv_Logo.svg';
import SpinsStar from '../../assets/Registration/Spins_Star.svg';
import { BRANDS } from '../Itv/types';
import { ItvHeader } from '../Itv/ItvHeader/ItvHeader';
import './styles/';
import { useUserContext } from '../../providers/UserProvider';
import { handleMarketingCookie } from '../../utils/marketing-preferences-helper';
import { Button } from '../shared/Button';
import { Icon } from '../shared/Icon';
import { useTranslations } from '../../hooks/useTranslationsHelper';
import { trackEvent } from '../../tracking/tracking';

export interface RegistrationProps {
  variant: BRANDS;
}

/**
 * Registration component.
 */
export const Registration: FC<RegistrationProps> = ({ variant }) => {
  const { isMobileDevice } = useDeviceInfo();
  const navigate = useNavigate();
  const { setTheme } = useUserContext();
  const images =
    variant === BRANDS.BINGO ? { logoImg: BingoLogo, starImg: BingoStar } : { logoImg: SpinsLogo, starImg: SpinsStar };

  const [abandonPromptOpen, setAbandonPromptOpen] = useState(false);
  const previousInput = useRef<string>('none');
  const lastFocused = useRef<string>('none');
  const abandonInFocusInputNameRef = useRef<string>();
  const inFocusFieldName = useRef<string>();
  const dispatch = useDispatch();

  const fieldShouldTriggersFormState: {
    [key: string]: boolean;
  } = useMemo(() => {
    return Object.keys(defaultRegistrationFormState).reduce((obj, key) => {
      return { ...obj, [key]: true };
    }, {});
  }, []);

  const firstFocus = useRef(true);

  const homePath = APP_URLS.itv[variant] || APP_URLS.itv.root;

  const handleQuitRegistration = (): void => {
    navigate(homePath);
  };

  const onFormControlFocus = (fieldName: string): void => {
    if (firstFocus.current) {
      firstFocus.current = false;
    }

    inFocusFieldName.current = fieldName;
    previousInput.current = lastFocused.current = fieldName;

    if (fieldShouldTriggersFormState[fieldName]) {
      fieldShouldTriggersFormState[fieldName] = false;
    }
  };

  const onFormControlBlur = (fieldName: string): void => {
    setTimeout(() => {
      if (inFocusFieldName.current === fieldName) {
        inFocusFieldName.current = undefined;
      }
    }, 500);
  };

  const onStepChange = (step: BaseRegistrationStepConfig): void => {
    // Step change handler - currently no state updates needed
  };

  const contextValue: RegistrationContextProps = {
    abandonPromptOpen,
    onFormControlFocus,
    onFormControlBlur,
    onCloseAbandonPrompt: () => {
      abandonInFocusInputNameRef.current = undefined;
      setAbandonPromptOpen(false);
    },
    onConfirmAbandon: handleQuitRegistration,
    onPostRegistrationLimitStep: () => {
      // Post registration limit step handler - currently no state updates needed
    },
    onAddressSearchFocus: () => {
      const AUTO_COMPLETE = 'autoComplete';

      inFocusFieldName.current = AUTO_COMPLETE;
    },
    onAddressSearchBlur: () => {
      setTimeout(() => (inFocusFieldName.current = undefined), 500);
    },
  };
  const { t } = useTranslations();

  useEffect(() => {
    try {
      trackEvent({ event: 'regstart' });
    } catch (trackingError) {
      console.error('Registration start tracking failed:', trackingError);
    }

    // reset to init values
    setTheme(variant);
    dispatch(setIsGeoLocationAllowed(true));
    dispatch(setIsErrorDuringGeoCheck(false));
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('oidc.user')) {
        localStorage.removeItem(key);
      }
    });

    // Handle marketing cookie on registration mount to capture UTM params from internal Links
    handleMarketingCookie();
  }, []);

  return (
    <div className={joinStrings(['registration', !isMobileDevice && 'gradient-cold', variant])}>
      <RegistrationContext.Provider value={contextValue}>
        {isMobileDevice && <ItvHeader variant={variant} />}
        <div className="registration__body">
          <Card>
            <CardContent>
              <div className="registration__back-button-wrapper">
                <Button
                  type="button"
                  variant="text"
                  onClick={handleQuitRegistration}
                  icon={<Icon variant="ItvArrowLeft" />}
                  iconPosition="left"
                  size="medium"
                  className="registration__back-button"
                >
                  {t('buttons.back')}
                </Button>
              </div>

              <RegistrationContainer onStepChange={onStepChange} />
            </CardContent>
          </Card>
          <div className="registration__content">
            {!isMobileDevice && (
              <div className="registration__logo">
                <div className="registration__logo-container">
                  <img src={images.logoImg} alt={`${variant} logo`} className="registration__logo-image" />
                  <img src={images.starImg} alt={`${variant} star`} className="registration__logo-star" />
                </div>
              </div>
            )}
          </div>
        </div>
      </RegistrationContext.Provider>
      <Footer isLangSwitcherDisplayed={false} isTimeDisplayed={false} />
    </div>
  );
};
